/**
 * Tenant Switcher Component
 *
 * Dropdown button for switching between user's tenants.
 * Displays current tenant and allows switching context.
 */

import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";

export function TenantSwitcher() {
  const { currentTenant, tenants, switchTenant } = useTenant();

  const handleSwitchTenant = async (tenantId: string) => {
    if (tenantId === currentTenant?.id) return;

    try {
      switchTenant(tenantId);
      toast.success("Tenant switched successfully");
    } catch (error) {
      toast.error("Failed to switch tenant");
      console.error("Error switching tenant:", error);
    }
  };

  // Render a placeholder with the same dimensions while data is not yet loaded.
  // Returning null causes a header layout shift (CLS) because the right section
  // grows by 200px once the tenant data arrives after Zustand persist hydration.
  if (!currentTenant || !tenants || tenants.length === 0) {
    return (
      <div
        className="w-[200px] h-9 rounded-md border border-input bg-background animate-pulse"
        aria-hidden="true"
      />
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-[200px] justify-between"
          aria-label="Switch tenant"
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{currentTenant.name}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuLabel>Switch Tenant</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tenants.map((tenant) => (
          <DropdownMenuItem
            key={tenant.id}
            onClick={() => handleSwitchTenant(tenant.id)}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 truncate">
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{tenant.name}</span>
              </div>
              {tenant.id === currentTenant.id && (
                <Check className="h-4 w-4 flex-shrink-0" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
